import { create } from "zustand";
import { tagsApi, type OrgTag } from "@services/api/tags";

interface TagStore {
  tags: OrgTag[];
  isLoading: boolean;
  _fetchedOrgId: string | undefined;

  fetchTags: (orgId: string) => Promise<void>;
  addTag: (tag: OrgTag) => void;
  updateTag: (tag: OrgTag) => void;
  removeTag: (id: string) => void;
  getTagsByScope: (scope: string) => OrgTag[];
  getTagById: (id: string) => OrgTag | undefined;
  getTagsByIds: (ids: string[]) => OrgTag[];
}

let _loadingOrgId: string | undefined;

export const useTagStore = create<TagStore>()((set, get) => ({
  tags: [],
  isLoading: false,
  _fetchedOrgId: undefined,

  fetchTags: async (orgId: string) => {
    if (get()._fetchedOrgId === orgId && get().tags.length > 0) return;
    // Chaque éditeur de tag charge la liste lui-même : une ligne de tableau par éditeur
    // ne doit pas lancer une requête chacune.
    if (get().isLoading && _loadingOrgId === orgId) return;
    _loadingOrgId = orgId;
    set({ isLoading: true });
    try {
      const tags = await tagsApi.list(orgId);
      set({ tags, isLoading: false, _fetchedOrgId: orgId });
    } catch (error) {
      console.error("Error fetching tags:", error);
      set({ isLoading: false });
    }
  },

  addTag: (tag: OrgTag) => {
    set((state) => ({ tags: [...state.tags, tag] }));
  },

  updateTag: (tag: OrgTag) => {
    set((state) => ({
      tags: state.tags.map((t) => (t.id === tag.id ? tag : t)),
    }));
  },

  removeTag: (id: string) => {
    set((state) => ({ tags: state.tags.filter((t) => t.id !== id) }));
  },

  getTagsByScope: (scope: string) => {
    return get().tags.filter((t) => t.scope === scope && t.active);
  },

  getTagById: (id: string) => {
    return get().tags.find((t) => t.id === id);
  },

  getTagsByIds: (ids: string[]) => {
    return ids.map((id) => get().tags.find((t) => t.id === id)).filter(Boolean) as OrgTag[];
  },
}));
